<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

const router = useRouter();
const loading = ref(false);

const roles = ref([
  { id: 'admin_informatico', nombre: 'Administrador Informático (TI)' },
  { id: 'admin_soporte', nombre: 'Administrador Soporte Técnico' },
  { id: 'admin_principal', nombre: 'Administrador Principal (Jefe Nacional)' },
  { id: 'admin_regional', nombre: 'Administrador Regional' },
  { id: 'usuario', nombre: 'Usuario Final' },
  { id: 'consultor', nombre: 'Consultor (Registro Temporal)' }
]);

const extensiones = ['LP', 'OR', 'PT', 'CB', 'SC', 'BN', 'PA', 'TJ', 'CH'];
const regionales = ref([]);
const gerencias = ref([]);
const secciones = ref([]); // NUEVO: Estado para Secciones
const cargos = ref([]);

// Ya no hay "username" vacío al inicio
const form = reactive({
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  ci: '',
  extension: '',
  telefono: '',
  rol: 'usuario',
  regional: '',
  gerencia: '',
  seccion: '', // NUEVO: Campo en el formulario
  cargo: ''
});

const cargarRegionales = async () => {
  try {
    const res = await api.get('regionales/');
    regionales.value = res.data.results || res.data;
  } catch (error) { console.error(error); }
};

// --- MECANISMO ANTI-CONFLICTOS (CASCADA COMPLETA) ---
const onRegionalChange = async () => {
  // 1. Limpiamos los campos inferiores si cambian la regional
  form.gerencia = '';
  form.seccion = '';
  form.cargo = '';
  gerencias.value = [];
  secciones.value = [];
  cargos.value = [];
  
  if (!form.regional) return;
  
  // 2. Traemos SOLO las gerencias de esa regional
  try {
    const res = await api.get(`gerencias/?regional=${form.regional}`);
    gerencias.value = res.data.results || res.data;
  } catch (error) { console.error(error); }
};

const onGerenciaChange = async () => {
  // 3. Limpiamos secciones y cargos si cambian la gerencia
  form.seccion = '';
  form.cargo = '';
  secciones.value = [];
  cargos.value = [];
  
  if (!form.gerencia) return;
  
  // 4. Traemos SOLO las secciones de esa gerencia
  try {
    const res = await api.get(`secciones/?gerencia=${form.gerencia}`);
    secciones.value = res.data.results || res.data;
  } catch (error) { console.error(error); }
};

const onSeccionChange = async () => {
  // 5. Limpiamos el cargo si cambian la sección
  form.cargo = '';
  cargos.value = [];
  
  if (!form.seccion) return;
  
  // 6. Traemos SOLO los cargos de esa sección
  try {
    const res = await api.get(`cargos/?seccion=${form.seccion}`);
    cargos.value = res.data.results || res.data;
  } catch (error) { console.error(error); }
};

const registrarUsuario = async () => {
  if (!form.email || !form.password || !form.regional || !form.gerencia || !form.seccion || !form.cargo) {
    return Swal.fire('Incompleto', 'Llene todos los campos obligatorios (*).', 'warning');
  }

  loading.value = true;
  try {
    // MAGIA AQUÍ: Clonamos el formulario y forzamos que username sea igual al email
    const payload = { ...form, username: form.email };

    await api.post('usuarios/registrar/', payload); 
    
    Swal.fire('¡Éxito!', 'Usuario creado correctamente.', 'success');
    router.push('/personal');
  } catch (error) {
    Swal.fire('Error', error.response?.data?.detail || 'El correo ya existe o los datos son inválidos.', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(cargarRegionales);
</script>

<template>
  <div class="container-fluid py-4 app-transition">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card card-custom shadow-lg border-0 border-radius-xl overflow-hidden">
          <div class="header-tamep d-flex align-items-center px-4 py-3 border-radius-top">
            <div class="logo-container-left me-3"><img :src="logo" class="logo-img"></div>
            <div class="border-start ps-3 border-white opacity-8">
              <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Registrar Nuevo Usuario</h6>
              <small class="text-white opacity-7">Creación directa de cuenta y permisos administrativos</small>
            </div>
          </div>
          <div class="card-body p-4">
            <form @submit.prevent="registrarUsuario">
              
              <h6 class="text-xs font-weight-bolder mb-3 text-primary text-uppercase">Datos de la Cuenta</h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Correo Electrónico (Usuario) *</label>
                  <input type="email" v-model="form.email" class="form-control input-custom" placeholder="ejemplo@tamep.bo" required>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Contraseña Temporal *</label>
                  <input type="password" v-model="form.password" class="form-control input-custom" required>
                </div>
                <div class="col-md-12 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Rol en el Sistema *</label>
                  <select v-model="form.rol" class="form-select select-custom" required>
                    <option v-for="rol in roles" :key="rol.id" :value="rol.id">{{ rol.nombre }}</option>
                  </select>
                </div>
              </div>

              <hr class="horizontal border-custom-divider my-4">

              <h6 class="text-xs font-weight-bolder mb-3 text-primary text-uppercase">Datos Personales</h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Nombres</label>
                  <input type="text" v-model="form.first_name" class="form-control input-custom">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Apellidos</label>
                  <input type="text" v-model="form.last_name" class="form-control input-custom">
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Cédula de Identidad</label>
                  <input type="text" v-model="form.ci" class="form-control input-custom">
                </div>
                <div class="col-md-2 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Ext.</label>
                  <select v-model="form.extension" class="form-select select-custom">
                    <option value="">--</option>
                    <option v-for="ext in extensiones" :key="ext" :value="ext">{{ ext }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Teléfono</label>
                  <input type="text" v-model="form.telefono" class="form-control input-custom">
                </div>
              </div>

              <hr class="horizontal border-custom-divider my-4">

              <h6 class="text-xs font-weight-bolder mb-3 text-primary text-uppercase">Asignación Corporativa</h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Regional *</label>
                  <select v-model="form.regional" @change="onRegionalChange" class="form-select select-custom" required>
                    <option value="" disabled>Seleccione Regional</option>
                    <option v-for="reg in regionales" :key="reg.id" :value="reg.id">{{ reg.nombre }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Gerencia *</label>
                  <select v-model="form.gerencia" @change="onGerenciaChange" class="form-select select-custom" :disabled="!form.regional" required>
                    <option value="" disabled>Seleccione Gerencia</option>
                    <option v-for="ger in gerencias" :key="ger.id" :value="ger.id">{{ ger.nombre }}</option>
                  </select>
                </div>
                
                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-info">Sección / Área *</label>
                  <select v-model="form.seccion" @change="onSeccionChange" class="form-select select-custom border-info-light" :disabled="!form.gerencia" required>
                    <option value="" disabled>Seleccione Sección</option>
                    <option v-for="sec in secciones" :key="sec.id" :value="sec.id">{{ sec.nombre }}</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label text-xs font-weight-bold text-custom-muted">Cargo Operativo *</label>
                  <select v-model="form.cargo" class="form-select select-custom" :disabled="!form.seccion" required>
                    <option value="" disabled>Seleccione Cargo</option>
                    <option v-for="car in cargos" :key="car.id" :value="car.id">{{ car.nombre }}</option>
                  </select>
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4">
                <button type="submit" class="btn btn-dark btn-save-custom px-5 py-2 font-weight-bold shadow-sm" :disabled="loading">
                  <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-user-plus'"></i> 
                  <span class="ms-2">{{ loading ? 'GUARDANDO...' : 'GUARDAR USUARIO' }}</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Transiciones Generales */
.app-transition { transition: all 0.3s ease; }
.form-label { margin-bottom: 0.2rem; }

/* Identidad Visual TAMEP (Tema Claro Default) */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }
.logo-container-left { background: white; padding: 5px 12px; border-radius: 8px; }
.logo-img { max-height: 35px; }

/* Variables Claras */
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.border-custom-divider { border-color: #e9ecef !important; transition: border-color 0.3s ease; }

.text-title-custom { color: #344767; transition: color 0.3s ease; }
.text-custom-muted { color: #8392ab; transition: color 0.3s ease; }

/* Inputs y Selects */
.input-custom, .select-custom { 
  background-color: #ffffff; 
  color: #495057; 
  border-color: #d2d6da; 
  transition: all 0.3s ease; 
}
.input-custom:focus, .select-custom:focus {
  border-color: #003366;
  box-shadow: 0 0 0 2px rgba(0, 51, 102, 0.1);
}
.border-info-light { border-color: #17a2b880 !important; }

select:disabled, input:disabled {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: not-allowed;
}


</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom { 
  background-color: #1e1e1e !important; 
  box-shadow: 0 4px 15px rgba(0,0,0,0.5) !important; 
  border: 1px solid #2d2d2d !important; 
}
.dark-version .border-custom-divider, .dark-mode .border-custom-divider { border-color: #333333 !important; }

.dark-version .text-title-custom, .dark-mode .text-title-custom { color: #ffffff !important; }
.dark-version .text-custom-muted, .dark-mode .text-custom-muted { color: #aaaaaa !important; }
.dark-version .text-primary, .dark-mode .text-primary { color: #4dabf7 !important; }

/* Formularios en Oscuro */
.dark-version .input-custom,
.dark-version .select-custom,
.dark-mode .input-custom,
.dark-mode .select-custom { 
  background-color: #2a2a2a !important; 
  color: #e0e0e0 !important; 
  border-color: #444444 !important; 
}
.dark-version .input-custom:focus,
.dark-version .select-custom:focus,
.dark-mode .input-custom:focus,
.dark-mode .select-custom:focus { 
  border-color: #4dabf7 !important; 
  box-shadow: 0 0 0 2px rgba(77, 171, 247, 0.2) !important; 
}

/* Campos Deshabilitados en Oscuro */
.dark-version select:disabled, 
.dark-version input:disabled,
.dark-mode select:disabled, 
.dark-mode input:disabled {
  background-color: #1a1a1a !important;
  color: #666 !important;
  border-color: #333 !important;
  opacity: 1;
}

/* Botones en Oscuro */
.dark-version .btn-save-custom, .dark-mode .btn-save-custom { 
  background-color: #4dabf7 !important; 
  color: #121212 !important; 
  border: none !important; 
}
.dark-version .btn-save-custom:hover:not(:disabled), .dark-mode .btn-save-custom:hover:not(:disabled) { 
  background-color: #3b82f6 !important; 
  color: #ffffff !important; 
}
</style>